import { FC, MouseEvent, useMemo, useState, useCallback } from 'react'
import { faUserPlus, faCheck } from '@document-crunch/pro-regular-svg-icons'
import {
  Avatar,
  Icon,
  IconColor,
  Tooltip,
} from '@dc-shared-web/components/base'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@dc-shared-web/components/shadcn'
import { AssignmentUser } from '@dc-shared-web/hooks/api/assignment'

type AssignUserProps = {
  addUser: (userUuid: string) => void
  inputPlaceholder: string
  removeUser: (userUuid: string) => void
  selectedUserUuid: string | null | undefined
  users: AssignmentUser[]
  disabled: boolean
  onDropdownOpen?: () => void
  onUserAssigned?: (userName: string) => void
  onUserUnassigned?: () => void
}

export const AssignUser: FC<AssignUserProps> = ({
  addUser,
  inputPlaceholder,
  removeUser,
  selectedUserUuid,
  users,
  disabled,
  onDropdownOpen,
  onUserAssigned,
  onUserUnassigned,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const assignedUser = useMemo(
    () => users.find((user) => user.user_uuid === selectedUserUuid),
    [users, selectedUserUuid]
  )

  const onTriggerClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()

      setDropdownOpen(!dropdownOpen)
      if (!dropdownOpen) {
        onDropdownOpen?.()
      }
    },
    [dropdownOpen, onDropdownOpen]
  )

  const onUserSelect = useCallback(
    (userUuid: string) => {
      const selectedUser = users.find((user) => user.user_uuid === userUuid)
      addUser(userUuid)
      onUserAssigned?.(formatUsername(selectedUser!))

      setDropdownOpen(false)
    },
    [addUser, onUserAssigned, users]
  )

  const onUnassignUser = useCallback(
    (userUuid: string) => {
      removeUser(userUuid)
      onUserUnassigned?.()

      setDropdownOpen(false)
    },
    [removeUser, onUserUnassigned]
  )

  /* Ideally we could reuse the default filter from cmdk but our current version (0.2.1) does not export it. We would need to upgrade but this has many impacts as it would require upgrading shadcn and there is a high risk of breaking changes. */
  const onFilter = useCallback((value: string, search: string) => {
    /* We appended the user_uuid to the value to avoid breaking keyboard navigation so we need to split it to get the name we want to filter on. */
    const [name] = value.split('|')

    const userName = name.toLowerCase()
    const searchTerm = search.toLowerCase()

    return userName.includes(searchTerm) ? 1 : 0
  }, [])

  return (
    <Tooltip
      content={
        assignedUser
          ? `Assigned to ${formatUsername(assignedUser)}`
          : 'Assign user'
      }
      withArrow
      active={!dropdownOpen}
    >
      <div>
        <Popover
          /* With this prop, interaction with outside elements will be disabled. */
          modal
          open={dropdownOpen}
          onOpenChange={disabled ? undefined : setDropdownOpen}
          data-testid='assign-user-trigger'
        >
          <PopoverTrigger asChild disabled={disabled}>
            <div
              className={
                disabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer'
              }
            >
              {assignedUser ? (
                <div onClick={disabled ? undefined : onTriggerClick}>
                  <Avatar
                    src={assignedUser.picture}
                    fallback={`${assignedUser.firstname.charAt(0)}${assignedUser.lastname.charAt(0)}`}
                    fallbackColor='blue'
                    size='sm'
                  />
                </div>
              ) : (
                <AssignUserEmptyTrigger />
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent className='tw-w-64 tw-p-0'>
            <Command
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
              }}
              filter={onFilter}
            >
              <CommandInput placeholder={inputPlaceholder} />
              <CommandList className='tw-max-h-52'>
                <CommandEmpty>No results</CommandEmpty>
                {users.length > 0 && (
                  <CommandGroup>
                    {selectedUserUuid && (
                      <CommandItem
                        onSelect={() => {
                          onUnassignUser(selectedUserUuid)
                        }}
                        className='tw-cursor-pointer data-[selected]:tw-bg-gray-100 tw-flex tw-items-center tw-gap-2'
                      >
                        <AssignUserEmptyTrigger />
                        <p>Unassigned</p>
                      </CommandItem>
                    )}
                    {users.map((user) => (
                      <CommandItem
                        key={user.user_uuid}
                        /* We need to add the user_uuid otherwise keyboard navigation breaks for users with same name but different uuid. */
                        value={`${formatUsername(user)}|${user.user_uuid}`}
                        onSelect={() => {
                          onUserSelect(user.user_uuid)
                        }}
                        className='tw-cursor-pointer data-[selected]:tw-bg-gray-100 tw-flex tw-items-center tw-gap-2'
                      >
                        <Avatar
                          src={user.picture}
                          fallback={`${user.firstname.charAt(0)}${user.lastname.charAt(0)}`}
                          fallbackColor='blue'
                          size='sm'
                        />
                        <p className='tw-flex-1 tw-truncate'>
                          {formatUsername(user)}
                        </p>
                        {selectedUserUuid === user.user_uuid && (
                          <Icon icon={faCheck} color={IconColor.tertiary} />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </Tooltip>
  )
}

const AssignUserEmptyTrigger = () => {
  return (
    <div className='tw-rounded-full tw-size-8 tw-flex tw-items-center tw-justify-center tw-border tw-border-gray-200 tw-border-dashed tw-bg-white hover:tw-bg-gray-50'>
      <Icon
        icon={faUserPlus}
        color={IconColor.iconMuted}
        className='tw-size-4'
      />
    </div>
  )
}

const formatUsername = (user: AssignmentUser) =>
  `${user.firstname} ${user.lastname}`
